import React from "react";
import { ConfigProvider, Pagination } from "antd";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";
import cn from "../../utilities/tailwind-merge";

const SPACED_PAGINATION_WRAPPER =
    "[&_.ant-pagination]:flex [&_.ant-pagination]:flex-wrap [&_.ant-pagination]:items-center [&_.ant-pagination]:gap-2 " +
    "[&_.ant-pagination-item]:!m-0 [&_.ant-pagination-prev]:!m-0 [&_.ant-pagination-next]:!m-0 " +
    "[&_.ant-pagination-jump-prev]:!m-0 [&_.ant-pagination-jump-next]:!m-0 " +
    "[&_.ant-pagination-item]:flex [&_.ant-pagination-item]:h-9 [&_.ant-pagination-item]:min-w-9 [&_.ant-pagination-item]:items-center [&_.ant-pagination-item]:justify-center " +
    "[&_.ant-pagination-item]:rounded-lg [&_.ant-pagination-item]:border [&_.ant-pagination-item]:border-[#E5E7EB] " +
    "[&_.ant-pagination-item:not(.ant-pagination-item-active)]:!bg-white " +
    "[&_.ant-pagination-item-active]:!border-[#5D5DF6] [&_.ant-pagination-item-active]:!bg-[#5D5DF6] " +
    "[&_.ant-pagination-item-active_a]:!text-white [&_.ant-pagination-item-active_.ant-pagination-item-link]:!text-white " +
    "[&_.ant-pagination-prev]:flex [&_.ant-pagination-prev]:h-9 [&_.ant-pagination-prev]:min-w-9 [&_.ant-pagination-prev]:items-center [&_.ant-pagination-prev]:justify-center " +
    "[&_.ant-pagination-prev]:rounded-lg [&_.ant-pagination-prev]:border [&_.ant-pagination-prev]:border-[#E5E7EB] [&_.ant-pagination-prev]:!bg-white " +
    "[&_.ant-pagination-next]:flex [&_.ant-pagination-next]:h-9 [&_.ant-pagination-next]:min-w-9 [&_.ant-pagination-next]:items-center [&_.ant-pagination-next]:justify-center " +
    "[&_.ant-pagination-next]:rounded-lg [&_.ant-pagination-next]:border [&_.ant-pagination-next]:border-[#E5E7EB] [&_.ant-pagination-next]:!bg-white " +
    "[&_.ant-pagination-jump-prev]:flex [&_.ant-pagination-jump-prev]:h-9 [&_.ant-pagination-jump-prev]:min-w-9 [&_.ant-pagination-jump-prev]:items-center [&_.ant-pagination-jump-prev]:justify-center " +
    "[&_.ant-pagination-jump-next]:flex [&_.ant-pagination-jump-next]:h-9 [&_.ant-pagination-jump-next]:min-w-9 [&_.ant-pagination-jump-next]:items-center [&_.ant-pagination-jump-next]:justify-center " +
    "[&_.ant-pagination]:w-full [&_.ant-pagination]:max-w-full [&_.ant-pagination]:justify-end " +
    "[&_.ant-pagination-total-text]:!mr-0 [&_.ant-pagination-total-text]:shrink-0 " +
    "[&_.ant-pagination-options]:!inline-flex [&_.ant-pagination-options]:shrink-0 [&_.ant-pagination-options]:!ml-0";

const CustomPagination = ({
    totalItem,
    pageSize,
    setPageSize,
    currentPage,
    setCurrentPage,
    pageSizeOptions = [10, 20, 30, 50],
    showSizeChanger = true,
    variant = "default",
}) => {
    const { t } = useTranslation("customers");
    const isSpaced = variant === "spaced";

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handlePageSizeChange = (_current, size) => {
        if (size === pageSize) {
            return;
        }
        setPageSize(size);
        setCurrentPage(1);
    };

    const theme = {
        token: {
            colorBgContainer: isSpaced ? "#ffffff" : "#F2F2FA",
            colorBgElevated: isSpaced ? "#ffffff" : "#F2F2FA",
        },
        components: {
            Select: {
                optionSelectedBg: "rgb(94, 93, 255, 0.2)",
            },
            ...(isSpaced && {
                Pagination: {
                    itemActiveBg: "#5D5DF6",
                    itemActiveColor: "#ffffff",
                    colorPrimary: "#5D5DF6",
                    colorPrimaryHover: "#4B4BE0",
                },
            }),
        },
    };

    return (
        <ConfigProvider theme={theme}>
            <div className={cn(isSpaced && "flex w-full min-w-0 justify-end", isSpaced && SPACED_PAGINATION_WRAPPER)}>
                <Pagination
                    total={totalItem}
                    size="small"
                    responsive={!isSpaced}
                    showLessItems={isSpaced}
                    align={isSpaced ? "end" : undefined}
                    showTotal={(total) => `${t("total")} ${total} ${t("items")}`}
                    pageSize={pageSize}
                    current={currentPage}
                    onChange={handlePageChange}
                    onShowSizeChange={handlePageSizeChange}
                    showSizeChanger={showSizeChanger}
                    pageSizeOptions={pageSizeOptions}
                />
            </div>
        </ConfigProvider>
    );
};

CustomPagination.propTypes = {
    totalItem: PropTypes.number.isRequired,
    pageSize: PropTypes.number.isRequired,
    setPageSize: PropTypes.func.isRequired,
    currentPage: PropTypes.number.isRequired,
    setCurrentPage: PropTypes.func.isRequired,
    pageSizeOptions: PropTypes.arrayOf(PropTypes.number),
    showSizeChanger: PropTypes.bool,
    variant: PropTypes.oneOf(["default", "spaced"]),
};

export default CustomPagination;
